import { Flex, useBreakpointValue } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { Key, useEffect, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { useExperimentsState } from '~/atoms/ExperimentsState'
import { StorageKeys } from '~/atoms/StorageKeys'
import { useUserState } from '~/atoms/UserState'
import {
  SlidingPanelBreak,
  ToggleListButton,
} from '~/components/layout/ResponsivePage'
import useElectronComms from '~/hooks/useElectronComms'
import { ApplicationRoutes } from '~/hooks/useRouting'
import {
  SiteNavigationSizes,
  useSiteNavigationStyle,
} from '~/pages/Home/SiteNavigation/componentStyles/SiteNavigation.style'
import { SiteNavigationCollapseTab } from '~/pages/Home/SiteNavigation/shared/SiteNavigationCollapseTab'
import { CallsTab } from '~/pages/Home/SiteNavigation/tabImplementations/CallsTab'
import { ContactsTab } from '~/pages/Home/SiteNavigation/tabImplementations/ContactsTab'
import { FaxTab } from '~/pages/Home/SiteNavigation/tabImplementations/FaxTab'
import { MessagingTab } from '~/pages/Home/SiteNavigation/tabImplementations/MessagingTab'
import { VoicemailTab } from '~/pages/Home/SiteNavigation/tabImplementations/VoicemailTab'
import { WebPhoneTab } from '~/pages/Home/SiteNavigation/tabImplementations/WebPhoneTab'
import { ActiveCallsBlock } from '~/pages/WebPhone/ActiveCallBlock'
import { isUserAdmin } from '~/utils/auth'
import { isElectron } from '~/utils/common'

//TODO add back in future release
import { DashboardsTab } from './tabImplementations/DashboardsTab'

interface SiteNavigationLocalStorage {
  isUserCollapsed?: boolean
}

const navigationLocalStorage = atomWithStorage<SiteNavigationLocalStorage>(
  StorageKeys.SiteNav,
  {
    isUserCollapsed: undefined,
  },
)

export const SiteNavigation = () => {
  const {
    initDeepLinkListener,
    initSettingsHotkeyListener,
    initGetExperimentsListener,
    initGoToMessagingListener,
    initGetPopupWindowDimensionsListener,
    initSetPopupWindowDimensionsListener,
  } = useElectronComms()
  const location = useLocation()
  const pathname = location.pathname
  const isBreakpointCollapsed = useBreakpointValue(
    { base: true, md: false },
    { ssr: false },
  )
  const slidingPanelBreak = useBreakpointValue(SlidingPanelBreak, {
    ssr: false,
  })
  const [_ls, _setLs] = useAtom(navigationLocalStorage)

  const {
    enhancedCallLogsEnabled: hasNewCallsAccess,
    agentStatusesEnabled: callCenterEnabled,
    userRoles,
  } = useUserState({
    canAutoRefresh: true,
  })

  const isUserCollapsed = _ls.isUserCollapsed
  const setIsUserCollapsed = (isCollapsed?: boolean) => {
    _setLs({ isUserCollapsed: isCollapsed })
  }

  const {
    experimentState: { dashboardsEnabled },
  } = useExperimentsState()

  //prettier-ignore
  const isCollapsed =
    isUserCollapsed !== undefined
      ? isUserCollapsed
      : isBreakpointCollapsed

  const getSiteNavigationSize = () => {
    if (slidingPanelBreak) {
      return SiteNavigationSizes.Small
    }
    if (isCollapsed) {
      return SiteNavigationSizes.Collapsed
    }
    return SiteNavigationSizes.Normal
  }

  const style = useSiteNavigationStyle({
    size: getSiteNavigationSize(),
  })

  const navigate = useNavigate()

  const handleSetSelectedTab = (id?: Key) => {
    navigate(id as ApplicationRoutes)
  }

  const handleCollapseClicked = () => {
    setIsUserCollapsed(true)
  }

  const handleExpandClicked = () => {
    setIsUserCollapsed(undefined)
  }

  useEffect(() => {
    if (!isElectron) {
      return
    }

    const removeDeepLinkListener = initDeepLinkListener(navigate)
    const removeSettingsHotkeyListener = initSettingsHotkeyListener(navigate)
    const removeGetExperimentsListener = initGetExperimentsListener()
    const removeGoToMessagingListener = initGoToMessagingListener(navigate)
    const removeGetPopupWindowDimensionsListener =
      initGetPopupWindowDimensionsListener()
    const removeSetPopupWindowDimensionsListener =
      initSetPopupWindowDimensionsListener()

    return () => {
      removeDeepLinkListener()
      removeSettingsHotkeyListener()
      removeGetExperimentsListener()
      removeGoToMessagingListener()
      removeGetPopupWindowDimensionsListener()
      removeSetPopupWindowDimensionsListener()
    }
  }, [])

  const pathCheck = (path: string) => {
    if (path === ApplicationRoutes.CALLS && pathname === '/') {
      return true
    }
    if (path[0] === '/') {
      // remove leading slash
      path = path.slice(1)
      return pathname.includes(path)
    }
    return pathname.includes(path)
  }
  const callsTabContent = useMemo(() => {
    return (
      <CallsTab
        id={ApplicationRoutes.CALLS}
        isSelected={pathCheck(ApplicationRoutes.CALLS)}
        onClick={handleSetSelectedTab}
        isCollapsed={isCollapsed}
      />
    )
  }, [hasNewCallsAccess, pathname, isCollapsed])

  const siteNavigationTabs = useMemo(() => {
    return (
      <>
        {callsTabContent}
        <VoicemailTab
          id={ApplicationRoutes.VOICEMAIL}
          isSelected={pathCheck(ApplicationRoutes.VOICEMAIL)}
          onClick={handleSetSelectedTab}
          isCollapsed={isCollapsed}
        />
        <MessagingTab
          id={ApplicationRoutes.MESSAGING}
          isSelected={pathCheck(ApplicationRoutes.MESSAGING)}
          onClick={handleSetSelectedTab}
          isCollapsed={isCollapsed}
        />
        <Link
          to={`${ApplicationRoutes.FAX}`}
          style={{
            width: isCollapsed ? 'auto' : '100%',
          }}
        >
          <FaxTab
            id={ApplicationRoutes.FAX}
            isSelected={pathCheck(ApplicationRoutes.FAX)}
            onClick={() => console.log('noop')}
            isCollapsed={isCollapsed}
          />
        </Link>
        <ContactsTab
          id={ApplicationRoutes.CONTACTS}
          isSelected={pathCheck(ApplicationRoutes.CONTACTS)}
          onClick={handleSetSelectedTab}
          isCollapsed={isCollapsed}
        />
        <WebPhoneTab
          id={ApplicationRoutes.WEB_PHONE}
          isSelected={pathCheck(ApplicationRoutes.WEB_PHONE)}
          onClick={handleSetSelectedTab}
          isCollapsed={isCollapsed}
        />
        {/* TODO remove this check once we have non call center related dashboards available */}
        {(dashboardsEnabled ||
          (callCenterEnabled && isUserAdmin(userRoles))) && (
          <DashboardsTab
            id={ApplicationRoutes.DASHBOARDS}
            isSelected={pathCheck(ApplicationRoutes.DASHBOARDS)}
            onClick={handleSetSelectedTab}
            isCollapsed={isCollapsed}
          />
        )}
      </>
    )
  }, [isCollapsed, callsTabContent, pathname])

  return (
    <Flex sx={style.container}>
      <Flex sx={style.tabWrapper}>
        <ToggleListButton />
        {siteNavigationTabs}
      </Flex>

      <Flex sx={style.footerWrapper}>
        <ActiveCallsBlock isCollapsed={isCollapsed} />
        <SiteNavigationCollapseTab
          id={ApplicationRoutes.SETTINGS}
          isCollapsed={isCollapsed}
          isSelected={pathname.includes(ApplicationRoutes.SETTINGS)}
          hideExpand={isBreakpointCollapsed}
          onCollapseClicked={handleCollapseClicked}
          onExpandClicked={handleExpandClicked}
          onSettingsClicked={handleSetSelectedTab}
        />
      </Flex>
    </Flex>
  )
}
